import "@progress/kendo-ui/js/kendo.grid";
import * as ko from "knockout";
import * as _ from "lodash";
import * as Constants from "../common/constants";
import { createWebServiceDataSource } from "../common/data";
import * as Global from "../common/global";
import * as GridUtil from "../common/gridFilter";
import * as UI from "../common/ui";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import { ICustomerSettings } from "../interfaces/user";

// classes used to display a list of appointments
// parameters for filtering the list
interface IAppointmentListFilter {
    SearchText: string;
    SearchAllFields: boolean;
}

interface IViewModel {
    searchText: KnockoutObservable<string | undefined>;
    sort: KnockoutObservableArray<kendo.data.DataSourceSortItem>;
    filter: KnockoutObservable<kendo.data.DataSourceFilter>;
    exportToExcelUrl: string;
    searchAllFields: KnockoutObservable<boolean>;
}

export class TasksController implements IController {
    // private properties

    private viewModel: IViewModel;

    // controls
    private grid: kendo.ui.Grid;
    private dataSource: kendo.data.DataSource;
    private gridFilterControl: GridUtil.GridFilterControl;

    // references to DOM elements
    private container: JQuery;
    private gridContainer: JQuery;

    private customerSettings: ICustomerSettings[];
    private dateFormat: string;
    private timeFormat: string;
    // public methods

    // outputs the control to the HTML
    constructor() {
        const me: TasksController = this;
        const app: IApplication = Global.app();
        me.customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        this.dateFormat = UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
        this.timeFormat = UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);
    }

    // returns a jQuery promise.
    public render(container: JQuery): JQueryPromise<any> {
        const me: TasksController = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        me.container = container;
        me.gridContainer = container.find("div[data-id='grid']");

        // create the grid
        me.dataSource = new kendo.data.DataSource({
            pageSize: 100,
        });

        let descriptionTemplateHtml = $("#template_appointments_description_nolink").html();
        if (app.userSession!.Permissions.indexOf(Constants.PERMISSION_VIEW_TARGETS) >= 0) {
            descriptionTemplateHtml = $("#template_appointments_description").html();
        }

        let templateNameTemplateHtml = $("#template_appointments_templatename_nolink").html();
        if (app.userSession!.Permissions.indexOf(Constants.PERMISSION_EDIT_APPOINTMENTS) >= 0) {
            templateNameTemplateHtml = $("#template_appointments_templatename").html();
        }

        let runReportTemplateHtml = $("#template_appointments_report_nolink").html();
        if (app.userSession!.Permissions.indexOf(Constants.PERMISSION_RUN_REPORTS) >= 0) {
            runReportTemplateHtml = $("#template_appointments_report").html();
        }

        me.grid = me.gridContainer.kendoGrid({
            dataSource: me.dataSource,
            selectable: false,
            sortable: true,
            filterable: true,
            pageable: true,
            columns: [
                {
                    title: "Date",
                    field: "AppointmentDate",
                    template: `#= AppointmentDate === null ? " " : kendo.toString(AppointmentDate, "${UI.getKendoDateFormat(me.dateFormat)}")#`,
                    width: "120px",
                    filterable: {
                        ui: (element: any): any => {
                            element.kendoDatePicker({
                                format: UI.getKendoDateFormat(me.dateFormat),
                            });
                        },
                    },
                },
                {
                    title: "Time",
                    field: "AppointmentDate",
                    template: `#= AppointmentDate === null ? " " : kendo.toString(AppointmentDate, "${UI.getKendoTimeFormat(me.timeFormat)}")#`,
                    width: "120px",
                    filterable: false,
                    sortable: false,
                },
                { title: "Type", field: "TemplateName", template: kendo.template(templateNameTemplateHtml) },
                { title: "Target", field: "Description", template: kendo.template(descriptionTemplateHtml) },
                { title: "Assigned To", field: "AssignedUserName" },
                { title: "Status", field: "StatusName", width: "150px" },
                { title: "Report", width: "110px", template: kendo.template(runReportTemplateHtml) },
            ],
            excel: {
                fileName: `${Constants.LABEL_APPOINTMENTS}.xlsx`,
                filterable: true,
                allPages: true,
            },
        }).data("kendoGrid");

        me.viewModel = {
            searchText: ko.observable<string>(""),
            exportToExcelUrl: `${app.webServiceBaseURL}/api/appointments/export`,
            sort: ko.observableArray(),
            filter: ko.observable<kendo.data.DataSourceFilter>(),
            searchAllFields: ko.observable(false),
        };

        ko.applyBindings(me.viewModel, me.container[0]);

        me.gridFilterControl = new GridUtil.GridFilterControl(container.find("div[data-id='filter']"), {
            showTextSearch: true,
            showExportToExcel: true,
            showFilters: false,
            showRefresh: true,
            showSearchAllFields: true,
            filters: [],
            grid: me.grid,
            buttons: [{ name: `Export ${Constants.LABEL_APPOINTMENT} Detail`, iconClass: "fa fa-file-excel-o", onClick: this.exportAppointmentsToExcel.bind(me) }],
        });

        // bind to all link tags in the grid
        me.gridContainer.on("click", "a[data-id='link']", function(e: any): boolean {
            const path: string | undefined = $(this).attr("href");
            e.preventDefault();
            app.router().navigate(_.defaultTo<string>(path, ""), true);
            return false;
        });
        me.gridContainer.on("click", "a[data-id='tasklink']", function(e: any): boolean {
            const appointmentIDString: string | undefined = $(this).attr("href");
            const appointmentID = Number(appointmentIDString);
            e.preventDefault();
            app.pageManager().gotoTaskPage(appointmentID, false);
            return false;
        });

        deferred.resolve();

        return deferred.promise();
    }

    // put data into this control.
    // call after rendering the control
    public load(params: any[]): JQueryPromise<any> {
        const app: IApplication = Global.app();
        const me: TasksController = this;
        let dataSource: kendo.data.DataSource;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        dataSource = createWebServiceDataSource(
            "/api/Appointments/ForGrid",
            {
                id: "ID",
                fields: {
                    AppointmentID: { type: "long" },
                    StatusID: { type: "long" },
                    AppointmentDate: { type: "date" },
                    TargetID: { type: "long" },
                    Description: { type: "string" },
                    StatusName: { type: "string" },
                    AssignedUserName: { type: "string" },
                    TemplateName: { type: "string" },
                    URLFriendlyName: { type: "string" },
                    StatusIsOpen: {type: "boolean"},
                    StatusIsCompleted: {type: "boolean"},
                    StatusIsCancelled: {type: "boolean"},
                    ReportID: {type: "long"},
                },
            },
            (data: any) => {
                me.setDataFilter(data);
            },
            (status: number) => {
                if (status === Constants.HTTP_STATUS_UNAUTHORIZED) {
                    // user is not allowed to view this data, so close the page
                    app.pageManager().closeCurrentPage();
                }
                // for other errors, keep page open, allow user to retry.
            });

        // load data into the grid by calling a web service
        me.grid.setDataSource(dataSource);

        deferred.resolve();
        return deferred.promise();
    }

    public add(): void {
        const app = Global.app();
        if (app.userSession!.Permissions.indexOf(Constants.PERMISSION_CREATE_APPOINTMENTS) >= 0) {
            Global.app().router().navigate("/TaskAdd", true);
        }
    }

    public haveFieldsChanged(): boolean {
        // no editable fields
        return false;
    }

    public height(height: number): void {
        if (height > GridUtil.GRIDFILTERCONTROL_HEIGHT) {
            UI.resizeGrid(this.gridContainer, height - GridUtil.GRIDFILTERCONTROL_HEIGHT);
        }
    }

    // called when loaded page is redisplayed
    public refresh(): void {
        this.grid.dataSource.read();
    }

    // returns a unique ID for the control, so it can be redisplayed if
    // already open.  Used by the AppRouter class.
    public appRouterID(params: string[]): string {
        return Constants.LABEL_APPOINTMENTS;
    }

    // returns a title for the page
    public title(): string {
        return Constants.LABEL_APPOINTMENTS;
    }

    public subTitle(): string {
        return "";
    }

    private setDataFilter(data: IAppointmentListFilter): void {
        if (this.gridFilterControl != null) {
            const values: GridUtil.IGridFilterValues = this.gridFilterControl.getValues();
            data.SearchText = values.searchText!;
            data.SearchAllFields = values.searchAllFields!;
        }
    }

    private exportAppointmentsToExcel(): void {
        const values: GridUtil.IGridFilterValues = this.gridFilterControl.getValues();

        // convert any date filters into the json string so that the values are preserved correctly in the form post
        const filters = this.grid.dataSource.filter();
        if (filters && filters.filters) {
            for (const f of filters.filters) {
                const filter = f as any;
                if (_.isDate(filter.value)) {
                    filter.value = filter.value.toJSON();
                }
            }
        }

        this.viewModel.filter(filters);
        this.viewModel.sort(this.grid.dataSource.sort());
        this.viewModel.searchText(values.searchText);
        this.viewModel.searchAllFields(values.searchAllFields ? values.searchAllFields : false);
        this.container.find("form[name='exportAppointmentsForm']").submit();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/tasks.ts